import React, { useEffect, useMemo, useState } from "react";
import {
  FiBarChart2,
  FiBookOpen,
  FiClock,
  FiCpu,
  FiGitBranch,
  FiLayers,
  FiMail,
  FiMonitor,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../../SpotifyLandingPage/components/LanguageSwitcher";

const TASK_ICON_BY_WINDOW = {
  timeline: FiClock,
  comparativo: FiBarChart2,
  stacks: FiLayers,
  metodo: FiCpu,
  mantra: FiBookOpen,
  ia_operacional: FiCpu,
  gitflow: FiGitBranch,
  portfolio_contact: FiMail,
};

const TASK_THEME_BY_WINDOW = {
  timeline: "deep",
  comparativo: "forest",
  stacks: "amber",
  metodo: "wine",
  mantra: "forest",
  ia_operacional: "amber",
  gitflow: "gold",
  portfolio_contact: "sunset",
};

export default function DesktopTaskbar({
  appWindows,
  projects,
  openWindows,
  startMenuOpen,
  onToggleStartMenu,
  onOpenWindow,
  onFocusWindow,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation("aiPortfolio");
  const [devSubmenuOpen, setDevSubmenuOpen] = useState(false);
  const [aiSubmenuOpen, setAiSubmenuOpen] = useState(false);
  const [supportsHover, setSupportsHover] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    const updateHoverCapability = (event) => {
      setSupportsHover(event.matches);
    };

    setSupportsHover(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateHoverCapability);
      return () => mediaQuery.removeEventListener("change", updateHoverCapability);
    }

    mediaQuery.addListener(updateHoverCapability);
    return () => mediaQuery.removeListener(updateHoverCapability);
  }, []);
  const projectsById = useMemo(
    () =>
      projects.reduce((accumulator, project) => {
        accumulator[project.id] = project;
        return accumulator;
      }, {}),
    [projects]
  );
  const appWindowsById = useMemo(
    () =>
      appWindows.reduce((accumulator, item) => {
        accumulator[item.id] = item;
        return accumulator;
      }, {}),
    [appWindows]
  );
  const devMenuItems = useMemo(
    () => [appWindowsById.resume_summary, appWindowsById.timeline, appWindowsById.stacks].filter(Boolean),
    [appWindowsById]
  );
  const aiMenuItems = useMemo(
    () =>
      [appWindowsById.metodo, appWindowsById.comparativo, appWindowsById.mantra, appWindowsById.ia_operacional, appWindowsById.gitflow].filter(
        Boolean
      ),
    [appWindowsById]
  );
  const contactMenuItem = useMemo(() => appWindowsById.portfolio_contact, [appWindowsById]);

  const changeLanguage = (lng) => {
    const supported = ["pt", "en", "es"];
    const pathParts = location.pathname.split("/").filter(Boolean);
    const firstPartIsLang = supported.includes(pathParts[0]);
    const hasIAPortfolioPath = pathParts.includes("ia-portfolio");

    if (pathParts.length === 0 || (firstPartIsLang && pathParts.length === 1)) {
      i18n.changeLanguage(lng);
      navigate(`/${lng}/ia-portfolio${location.search}${location.hash}`);
      return;
    }

    if (firstPartIsLang) {
      pathParts[0] = lng;
    } else if (hasIAPortfolioPath) {
      pathParts.unshift(lng);
    } else {
      pathParts.splice(0, pathParts.length, lng, "ia-portfolio");
    }

    i18n.changeLanguage(lng);
    navigate(`/${pathParts.join("/")}${location.search}${location.hash}`);
  };

  return (
    <header className="taskbar">
      <div className="startWrap">
        <button type="button" className="startButton" onClick={onToggleStartMenu}>
          {t("taskbar.start")}
        </button>

        {startMenuOpen && (
          <div className="startMenu">
            <div
              className="startMenuWithSubmenu"
              onMouseEnter={supportsHover ? () => setDevSubmenuOpen(true) : undefined}
              onMouseLeave={supportsHover ? () => setDevSubmenuOpen(false) : undefined}
            >
              <button
                type="button"
                className="startMenuSubmenuRoot"
                onClick={() =>
                  setDevSubmenuOpen((prev) => {
                    const next = !prev;
                    if (next) {
                      setAiSubmenuOpen(false);
                    }
                    return next;
                  })
                }
                aria-expanded={devSubmenuOpen}
              >
                <strong>{t("taskbar.groups.dev.title", { defaultValue: "12 anos de dev" })}</strong>
                <span>{t("taskbar.groups.dev.subtitle", { defaultValue: "Currículo, Linha do Tempo e Stacks" })}</span>
              </button>

              {devSubmenuOpen && (
                <div className="startSubmenu">
                  {devMenuItems.map((item) => (
                    <button key={item.id} type="button" onClick={() => onOpenWindow(item.id)}>
                      <strong>{item.label}</strong>
                      <span>{item.subtitle}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div
              className="startMenuWithSubmenu"
              onMouseEnter={supportsHover ? () => setAiSubmenuOpen(true) : undefined}
              onMouseLeave={supportsHover ? () => setAiSubmenuOpen(false) : undefined}
            >
              <button
                type="button"
                className="startMenuSubmenuRoot"
                onClick={() =>
                  setAiSubmenuOpen((prev) => {
                    const next = !prev;
                    if (next) {
                      setDevSubmenuOpen(false);
                    }
                    return next;
                  })
                }
                aria-expanded={aiSubmenuOpen}
              >
                <strong>{t("taskbar.groups.ai.title", { defaultValue: "1 ano de IA" })}</strong>
                <span>{t("taskbar.groups.ai.subtitle", { defaultValue: "Método, Comparativos, Mantra e Gitflow" })}</span>
              </button>

              {aiSubmenuOpen && (
                <div className="startSubmenu">
                  {aiMenuItems.map((item) => (
                    <button key={item.id} type="button" onClick={() => onOpenWindow(item.id)}>
                      <strong>{item.label}</strong>
                      <span>{item.subtitle}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="startMenuDivider" />

            {contactMenuItem && (
              <button type="button" onClick={() => onOpenWindow(contactMenuItem.id)}>
                <strong>{contactMenuItem.label}</strong>
                <span>{contactMenuItem.subtitle}</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="taskItems">
        {openWindows.map((windowId) => {
          const isProject = windowId.startsWith("project-");
          const projectId = windowId.replace("project-", "");
          const project = isProject ? projectsById[projectId] : null;
          const Icon = !isProject ? TASK_ICON_BY_WINDOW[windowId] || FiMonitor : null;
          const theme = isProject ? project?.mockTheme : TASK_THEME_BY_WINDOW[windowId] || "deep";
          const label = isProject && project ? project.name : appWindowsById[windowId]?.label || windowId;

          return (
            <button
              key={windowId}
              type="button"
              className={`taskItemButton ${theme}`}
              onClick={() => onFocusWindow(windowId)}
              aria-label={t("taskbar.focusWindow", { label })}
              title={label}
            >
              {isProject && project ? (
                <span className="taskProjectIcon">{project.icon}</span>
              ) : (
                <Icon />
              )}
            </button>
          );
        })}
      </div>

      <div className="taskbarRight">
        <LanguageSwitcher
          onChange={changeLanguage}
          containerStyle={{ display: "flex", alignItems: "center", gap: "0.3rem", padding: 0 }}
          buttonStyle={{
            background: "rgba(0, 0, 0, 0.3)",
            borderRadius: "6px",
            padding: "0.1rem",
            cursor: "pointer",
            lineHeight: 0,
          }}
          flagWidth={30}
          flagHeight={20}
        />
        <div className="taskClock">
          <span>
            {new Date().toLocaleTimeString(t("taskbar.clockLocale"), {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      </div>
    </header>
  );
}
